<template>
	<!-- Loading State -->
	<div
		v-if="isLoading"
		class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4"
	>
		<div
			class="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-md w-full mx-2"
		>
			<div class="flex flex-col items-center space-y-4 sm:space-y-6">
				<!-- Nexus Logo/Icon -->
				<div
					class="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"
				>
					<i class="fas fa-bolt text-white animate-pulse"></i>
				</div>

				<!-- Loading Animation -->
				<div class="relative">
					<div
						class="w-10 h-10 sm:w-12 sm:h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"
					></div>
				</div>

				<!-- Loading Text -->
				<div class="text-center">
					<h3
						class="text-base sm:text-lg font-semibold text-gray-800 mb-1"
					>
						Nexus Insurance
					</h3>
					<p class="text-xs sm:text-sm text-gray-600">
						Processing your payment...
					</p>
				</div>

				<!-- Loading Progress -->
				<div class="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
					<div
						class="bg-gradient-to-r from-blue-600 to-indigo-600 h-1.5 sm:h-2 rounded-full animate-pulse"
						:style="`width: ${loadingProgress}%`"
					></div>
				</div>
			</div>
		</div>
	</div>

	<!-- Success State -->
	<div
		v-else
		class="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-4 sm:py-6 px-3 sm:px-4"
	>
		<div class="max-w-4xl mx-auto">
			<!-- Success Header -->
			<div class="text-center mb-6 sm:mb-8 animate-fade-in-up">
				<div
					class="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full shadow-lg mb-3 sm:mb-4 animate-bounce-once"
				>
					<i class="fas fa-check text-white"></i>
				</div>
				<h1
					class="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2"
				>
					Payment Successful!
				</h1>
				<p class="text-base sm:text-lg text-green-600 font-medium">
					Your insurance policy has been activated
				</p>
			</div>

			<!-- Main Content Grid -->
			<div
				class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8"
			>
				<!-- Order Summary Card -->
				<div
					class="lg:col-span-2 bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate-fade-in-up"
					style="animation-delay: 0.1s"
				>
					<!-- Card Header -->
					<div
						class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 sm:px-6 sm:py-4"
					>
						<div class="flex items-center space-x-2 sm:space-x-3">
							<i class="fas fa-file-alt text-white"></i>
							<h2
								class="text-lg sm:text-xl font-semibold text-white"
							>
								Policy Details
							</h2>
						</div>
					</div>

					<!-- Card Content -->
					<div class="p-4 sm:p-6">
						<div class="grid grid-cols-1 gap-3 sm:gap-4">
							<!-- Product Info -->
							<div class="space-y-3 sm:space-y-4">
								<div
									class="flex items-start space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg"
								>
									<div
										class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full mt-1.5 sm:mt-2"
									></div>
									<div class="flex-1 min-w-0">
										<p
											class="text-xs font-medium text-gray-500 uppercase tracking-wide"
										>
											Product
										</p>
										<p
											class="text-sm font-semibold text-gray-900 truncate"
										>
											{{
												selectedProduct
													? selectedProduct.productName
													: 'N/A'
											}}
										</p>
									</div>
								</div>

								<div
									class="flex items-start space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg"
								>
									<div
										class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full mt-1.5 sm:mt-2"
									></div>
									<div class="flex-1 min-w-0">
										<p
											class="text-xs font-medium text-gray-500 uppercase tracking-wide"
										>
											Premium
										</p>
										<p
											class="text-base sm:text-lg font-bold text-green-600"
										>
											KES
											{{
												selectCalculatedQuote &&
												selectCalculatedQuote.total
													? selectCalculatedQuote.total.toLocaleString()
													: 'N/A'
											}}
										</p>
									</div>
								</div>

								<div
									class="flex items-start space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg"
								>
									<div
										class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-500 rounded-full mt-1.5 sm:mt-2"
									></div>
									<div class="flex-1 min-w-0">
										<p
											class="text-xs font-medium text-gray-500 uppercase tracking-wide"
										>
											Vehicle Value
										</p>
										<p
											class="text-sm font-semibold text-gray-900"
										>
											KES
											{{
												selectQuotationData &&
												selectQuotationData.vehicleValue
													? selectQuotationData.vehicleValue.toLocaleString()
													: 'N/A'
											}}
										</p>
									</div>
								</div>
							</div>

							<!-- Coverage Info -->
							<div class="space-y-3 sm:space-y-4">
								<div
									class="flex items-start space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg"
								>
									<div
										class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-orange-500 rounded-full mt-1.5 sm:mt-2"
									></div>
									<div class="flex-1 min-w-0">
										<p
											class="text-xs font-medium text-gray-500 uppercase tracking-wide"
										>
											Cover Type
										</p>
										<p
											class="text-sm font-semibold text-gray-900 truncate"
										>
											{{
												selectedCoverType
													? selectedCoverType
															.charAt(0)
															.toUpperCase() +
														selectedCoverType.slice(
															1,
														)
													: 'N/A'
											}}
										</p>
									</div>
								</div>

								<div
									class="flex items-start space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg"
								>
									<div
										class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-indigo-500 rounded-full mt-1.5 sm:mt-2"
									></div>
									<div class="flex-1 min-w-0">
										<p
											class="text-xs font-medium text-gray-500 uppercase tracking-wide"
										>
											Cover Period
										</p>
										<p
											class="text-sm font-semibold text-gray-900"
										>
											{{
												selectQuotationData &&
												selectQuotationData.period
													? selectQuotationData.period +
														' Days'
													: 'N/A'
											}}
										</p>
									</div>
								</div>

								<div
									class="flex items-start space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg"
								>
									<div
										class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full mt-1.5 sm:mt-2"
									></div>
									<div class="flex-1 min-w-0">
										<p
											class="text-xs font-medium text-gray-500 uppercase tracking-wide"
										>
											Start Date
										</p>
										<p
											class="text-sm font-semibold text-gray-900"
										>
											{{
												selectQuotationData &&
												selectQuotationData.start_date
													? selectQuotationData.start_date
													: 'N/A'
											}}
										</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Action Cards -->
				<div class="space-y-4 sm:space-y-6">
					<!-- Next Steps Card -->
					<div
						class="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 animate-fade-in-up"
						style="animation-delay: 0.2s"
					>
						<div
							class="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4"
						>
							<div
								class="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center"
							>
								<svg
									class="w-4 h-4 sm:w-5 sm:h-5 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									></path>
								</svg>
							</div>
							<h3
								class="text-base sm:text-lg font-semibold text-gray-900"
							>
								Next Steps
							</h3>
						</div>
						<p
							class="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4"
						>
							Access your client portal to download your insurance
							certificate and manage your policy.
						</p>
						<a
							href="/newportal"
							class="w-full inline-flex items-center justify-center px-4 py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
						>
							<i class="fas fa-download mr-1.5 sm:mr-2"></i>
							Go to Client Portal
						</a>
					</div>

					<!-- Review Policy Card -->
					<div
						class="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 animate-fade-in-up"
						style="animation-delay: 0.25s"
					>
						<div
							class="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4"
						>
							<div
								class="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-lg flex items-center justify-center"
							>
								<i class="fas fa-star text-white"></i>
							</div>
							<h3
								class="text-base sm:text-lg font-semibold text-gray-900"
							>
								Review Your Policy
							</h3>
						</div>
						<p
							class="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4"
						>
							Help us improve by sharing your feedback.
						</p>
						<button
							@click="showReviewModal = true"
							class="w-full inline-flex items-center justify-center px-4 py-2.5 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
						>
							<i class="fas fa-star mr-1.5 sm:mr-2"></i>
							Write a Review
						</button>
					</div>

					<!-- Contact Support Card -->
					<div
						class="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl border border-gray-200 p-4 sm:p-6 animate-fade-in-up"
						style="animation-delay: 0.3s"
					>
						<div
							class="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3"
						>
							<div
								class="w-7 h-7 sm:w-8 sm:h-8 bg-gray-600 rounded-lg flex items-center justify-center"
							>
								<i class="fas fa-phone text-white"></i>
							</div>
							<h4 class="text-sm sm:font-medium text-gray-900">
								Need Help?
							</h4>
						</div>
						<p class="text-xs text-gray-600 mb-2 sm:mb-3">
							Contact our support team for assistance with your
							policy.
						</p>
						<div class="flex space-x-2">
							<button
								class="flex-1 text-xs px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
							>
								Chat
							</button>
							<button
								class="flex-1 text-xs px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
							>
								Call
							</button>
						</div>
					</div>

					<!-- System Info Card -->
					<div
						class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl sm:rounded-2xl border border-blue-100 p-3 sm:p-4 animate-fade-in-up"
						style="animation-delay: 0.4s"
					>
						<div
							class="flex items-center justify-between mb-1 sm:mb-2"
						>
							<h5
								class="text-xs sm:text-sm font-medium text-blue-900"
							>
								Nexus Insurance
							</h5>
							<div
								class="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full animate-pulse"
							></div>
						</div>
						<p class="text-xs text-blue-700 mb-1">
							{{ currentDateTime }}
						</p>
						<p class="text-xs text-blue-600">
							Transaction completed successfully
						</p>
					</div>
				</div>
			</div>

			<!-- Footer Section -->
			<div
				class="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6 animate-fade-in-up"
				style="animation-delay: 0.5s"
			>
				<div
					class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 text-center"
				>
					<div
						class="flex flex-col items-center space-y-1.5 sm:space-y-2"
					>
						<div
							class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-center"
						>
							<i class="fas fa-check-circle text-white"></i>
						</div>
						<h4 class="text-sm sm:font-semibold text-gray-900">
							Instant Coverage
						</h4>
						<p class="text-xs sm:text-sm text-gray-600">
							Your policy is active immediately
						</p>
					</div>

					<div
						class="flex flex-col items-center space-y-1.5 sm:space-y-2"
					>
						<div
							class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-400 to-blue-500 rounded-full flex items-center justify-center"
						>
							<i class="fas fa-lock text-white"></i>
						</div>
						<h4 class="text-sm sm:font-semibold text-gray-900">
							Secure Payment
						</h4>
						<p class="text-xs sm:text-sm text-gray-600">
							256-bit SSL encryption
						</p>
					</div>

					<div
						class="flex flex-col items-center space-y-1.5 sm:space-y-2"
					>
						<div
							class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-purple-400 to-purple-500 rounded-full flex items-center justify-center"
						>
							<i class="fas fa-headset text-white"></i>
						</div>
						<h4 class="text-sm sm:font-semibold text-gray-900">
							24/7 Support
						</h4>
						<p class="text-xs sm:text-sm text-gray-600">
							Always here to help you
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Review Modal -->
		<ReviewModal
			:isOpen="showReviewModal"
			@close="showReviewModal = false"
			@submit="handleReviewSubmit"
		/>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import ReviewModal from './ReviewModal.vue'; // Adjust the path as needed

export default {
	name: 'PaymentConfirmation',
	components: {
		ReviewModal,
	},
	data() {
		return {
			isLoading: true,
			loadingProgress: 0,
			currentDateTime: '',
			showReviewModal: false,
		};
	},
	computed: {
		...mapGetters('quotation', [
			'selectCalculatedQuote',
			'selectQuotationData',
			'selectSystemQuotation',
			'selectedQuotationReview',
		]),
		...mapGetters('productType', ['selectedProduct', 'selectedCoverType']),
		debugGetters() {
			console.log('Vuex Getters:', {
				selectCalculatedQuote: this.selectCalculatedQuote,
				selectQuotationData: this.selectQuotationData,
				selectedProduct: this.selectedProduct,
				selectedCoverType: this.selectedCoverType,
			});
			return true;
		},
	},
	mounted() {
		console.log('PaymentConfirmation mounted');
		this.simulateLoading();
		this.updateDateTime();
		setInterval(this.updateDateTime, 1000);
	},
	methods: {
		simulateLoading() {
			console.log('Starting simulateLoading');
			const interval = setInterval(() => {
				this.loadingProgress += Math.random() * 20;
				console.log('Loading Progress:', this.loadingProgress);
				if (this.loadingProgress >= 100) {
					this.loadingProgress = 100;
					clearInterval(interval);
					setTimeout(() => {
						this.isLoading = false;
						console.log(
							'Loading Complete, isLoading:',
							this.isLoading,
						);
						console.log('Review Policy card should now be visible');
					}, 500);
				}
			}, 200);
		},
		review() {
			return this.selectedQuotationReview;
		},
		updateDateTime() {
			const now = new Date();
			const options = {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
				second: '2-digit',
				hour12: true,
			};
			this.currentDateTime = now.toLocaleDateString('en-US', options);
		},
		handleReviewSubmit({ rating, message }) {
			console.log('Review submitted:', { rating, message });
			alert(`Review submitted: ${rating} stars, Message: ${message}`);
		},
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes bounce-once {
	0%,
	20%,
	50%,
	80%,
	100% {
		transform: translateY(0);
	}
	40% {
		transform: translateY(-10px);
	}
	60% {
		transform: translateY(-5px);
	}
}

.animate-fade-in-up {
	animation: fade-in-up 0.6s ease-out forwards;
	opacity: 0;
}

.animate-bounce-once {
	animation: bounce-once 1s ease-in-out;
}

.backdrop-blur-sm {
	backdrop-filter: blur(4px);
	-webkit-backdrop-filter: blur(4px);
}

/* Improved responsive adjustments */
@media (max-width: 640px) {
	.grid-cols-1.sm\\:grid-cols-2 {
		grid-template-columns: 1fr;
		gap: 0.75rem;
	}

	.text-3xl.sm\\:text-4xl {
		font-size: 1.75rem;
		line-height: 2rem;
	}
}

@media (max-width: 400px) {
	.p-4,
	.p-6 {
		padding: 0.75rem;
	}

	.space-y-3 > * + *,
	.space-y-4 > * + * {
		margin-top: 0.5rem;
	}

	.text-xs {
		font-size: 0.6875rem;
	}
}

/* Enhanced touch targets for mobile */
button,
a {
	touch-action: manipulation;
}

/* Prevent text size adjustment on orientation change */
html {
	-webkit-text-size-adjust: 100%;
	text-size-adjust: 100%;
}

/* Custom scrollbar for better aesthetics */
::-webkit-scrollbar {
	width: 6px;
}

::-webkit-scrollbar-track {
	background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}
</style>
